<template>
  <section class="carousel-rail">
    <div :id="containerId" class="carousel-viewport" @scroll="pinScroll">
      <div class="carousel-track" :style="trackStyle">
        <CarouselCell
          v-for="(series, index) of items"
          :key="`${series.slug}-${index}`"
          :series="series"
          :dimmed="index !== currentIndex"
          :id="itemId(index)"
          :tabindex="index"
          v-focus
          @focus="onCardFocused(index)"
          @up="handleUp"
          @down="handleDown"
          @left="handleLeft(index)"
          @right="handleRight(index)"
        />
      </div>
    </div>
    <div v-if="items.length > 1" class="carousel-dots">
      <span
        v-for="(series, index) of items"
        :key="`dot-${series.slug}-${index}`"
        :class="['carousel-dot', index === currentIndex ? 'current' : '']"
      ></span>
    </div>
  </section>
</template>

<script setup lang="ts">


import { ref, computed, watch, onMounted, nextTick, getCurrentInstance } from 'vue';
import { navigationService } from 'vue-spatialnavigation';
import { RailResponse, SeriesItem } from '@/types/cms';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { focusElementById } from '@/helpers/focus';
import CarouselCell from '@/components/cells/CarouselCell.vue';

const props = defineProps<{
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}>();

const { containerId, itemId, handleUp, handleDown, handleLeftFallback } =
  useRailNavigation(props);

const instance = getCurrentInstance();
const lazyLoad = instance?.proxy?.$lazyLoad;

const currentIndex = ref(0);
const stride = ref(0);

const items = computed(() =>
  props.rail.items
    .filter((item): item is SeriesItem => item.type === 'SERIES')
    .map((item) => item.series)
);

/**
 * Distance from one card's left edge to the next.
 *
 * Measured off the DOM rather than recomputed here, so the card width and the
 * gap stay defined once each in SCSS. `offsetLeft` is a layout value, so
 * neither the track's own transform nor the design canvas's affects it, and
 * this stays correct whatever the row's current position.
 */
const measureStride = () => {
  const first = document.getElementById(itemId(0));
  const second = document.getElementById(itemId(1));
  if (first && second) stride.value = second.offsetLeft - first.offsetLeft;
};

// The first card is centred by its own margin, so index 0 needs no shift and
// every other index is that many strides to the left.
const trackStyle = computed(() => ({
  transform: `translateX(-${currentIndex.value * stride.value}px)`,
}));

/**
 * Keeps the viewport's own scroll offset at zero.
 *
 * Nothing should ever scroll it — position is the track's transform — but the
 * row still overflows it in layout terms, which leaves it programmatically
 * scrollable. Focusing a card that is still mid-slide makes the browser reveal
 * it by scrolling the viewport, stacking a scroll offset on top of the
 * transform. Pinning it back is more general than predicting every caller;
 * `focus({ preventScroll: true })` would cover the focus case alone but needs
 * Chrome 64, above our Tizen 4 floor.
 */
const pinScroll = () => {
  const viewport = document.getElementById(containerId.value);
  if (viewport && viewport.scrollLeft !== 0) viewport.scrollLeft = 0;
};

const focusCard = (index: number) => {
  const targetId = itemId(index);
  if (!navigationService.getFocusElementById(targetId)) return;

  currentIndex.value = index;

  //Call helper with no scrolling
  focusElementById(targetId);

  // Still required below that, where `preventScroll` is ignored: in the same
  // task as the focus that may have caused a reveal-scroll, so the displaced
  // frame is never painted.
  pinScroll();
};

const handleLeft = (index: number) => {
  if (index === 0) {
    handleLeftFallback();
    return;
  }
  focusCard(index - 1);
};

const handleRight = (index: number) => {
  if (index >= items.value.length - 1) return;
  focusCard(index + 1);
};


const onCardFocused = (index: number) => {
  if (index === 0 && currentIndex.value !== 0) {
    nextTick(() => focusCard(currentIndex.value));
    return;
  }

  currentIndex.value = index;
};

const scanForLazyImages = () => {
  setTimeout(() => {
    const viewport = document.getElementById(containerId.value);
    if (viewport) lazyLoad?.(viewport);
  }, 200);
};

onMounted(() => {
  scanForLazyImages();
  measureStride();
});

//Rails are keyed on ID, so a rail whose items change is updated rather than remounted
watch(() => items.value.map((series) => series.slug).join(), () => {
  scanForLazyImages();
  nextTick(measureStride);

  // Polling can shrink the rail out from under us; a currentIndex past the end
  // would dim every card and light no dot.
  const lastIndex = items.value.length - 1;
  if (currentIndex.value > lastIndex) {
    currentIndex.value = Math.max(0, lastIndex);
  }
});
</script>

<style scoped lang="scss">
.carousel-rail {
  padding-bottom: 40px;
}

// Clips the row to the rail's width. Deliberately the only job it has: it holds
// no scroll position of its own, which is what `pinScroll` enforces.
.carousel-viewport {
  overflow: hidden;
}

.carousel-track {
  // Position relative so the cards' offsetLeft is measured against the track
  position: relative;
  display: flex;

  // The slide itself. Transform rather than scroll, so nothing outside this
  // component can interrupt it — see the note at the top of the script block.
  transition: transform 0.3s ease;
}

// Half the leftover width before the first card, which is what centres it at
// `translateX(0)`. Deliberately a percentage, not `100vw`: the rails sit inside
// NavNestedView's `.content`, right of a 160px sidebar, so the track is
// narrower than the viewport and a vw-based figure pushes every card off-centre
// by half the sidebar. A percentage resolves against the track's own content
// box, which is the width that actually matters.
//
// Nothing is needed after the last card: a transform has no scroll extent to
// run out of, so the row can slide as far as the last card needs.
.carousel-track > :first-child {
  margin-left: calc(50% - #{$carousel-card-width} / 2);
}

// Adjacent sibling rather than flex `gap`, which needs Chrome 84 — above our
// platform floor — and would also add a trailing gap past the last card.
.carousel-track > * + * {
  margin-left: $carousel-card-gap;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.carousel-dot {
  width: 16px;
  height: 16px;
  background-color: rgba($color-pink, 0.35);
  transition: width 0.2s ease-in-out, background-color 0.2s ease-in-out;

  & + & {
    margin-left: 16px;
  }

  &.current {
    width: 32px;
    background-color: $color-pink;
  }
}
</style>
